import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/pages/login";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PatientProtectedRoute from "./components/PatientProtectedRoute";
import { OfflineProvider } from "./offline/OfflineProvider";
import ErrorBoundary from "./components/common/ErrorBoundary";
import Error from "./components/pages/login/Error";
import ServerError from "./components/pages/login/ServerError";

// Layouts and Pages
import DashboardLayout from "./pages/dashboard/layout";
import Home from "./pages/Home";

// Accounting module — frontend pages not yet implemented.
// The backend module exists on the `feature/accounting` branch but the
// frontend dashboard/charts/journal/etc pages were never committed.
// Re-add the lazy() imports when those pages are built. The
// isModuleEnabled('accounting') guard ensures routes only render when
// the module flag is set.

// Patients
import PatientRegisterPage from "./features/patients/pages/PatientRegisterPage";
import PatientListPage from "./features/patients/pages/PatientListPage";
import PatientDetailPage from "./features/patients/pages/PatientDetailPage";
import PatientEditPage from "./features/patients/pages/PatientEditPage";

// Appointments
import { AppointmentListPage, AppointmentFormPage } from "./features/appointments";
import AppointmentDetailPage from "./features/appointments/pages/AppointmentDetailPage";

// Pharmacy
import { MedicationListPage, MedicationFormPage } from "./features/pharmacy";

// Insurance (for consultations)
import { InsuranceCompanyListPage, InsuranceCompanyFormPage, InsurancePlanListPage, InsurancePlanFormPage } from "./features/insurance";
import { NcfSequenceListPage, NcfSequenceFormPage } from "./features/ncf-sequences";
import { SupplierListPage, SupplierFormPage } from "./features/suppliers";

// Dashboard
import { AdminDashboardPage, DoctorDashboardPage } from "./features/dashboard";

// Clinical Dashboard
import { ClinicalDashboardPage } from "./features/clinical-dashboard";

// Login pages
import ForgotPassword from "./components/pages/login/ForgotPassword";
import Signup from "./components/pages/login/Signup";
import Register from "./components/pages/login/Register";
import ChangePassword from "./components/pages/login/ChangePassword";
import Profile from "./components/pages/login/Profile";
import EditProfile from "./components/pages/login/EditProfile";

// Services
import { ServiceListPage, ServiceFormPage } from "./features/services";

// Imaging
import { ImagingOrderListPage, ImagingOrderFormPage, ImagingResultFormPage, ImagingViewerPage } from "./features/imaging";

// Doctors
import DoctorListPage from "./features/doctors/pages/DoctorListPage";
import DoctorFormPage from "./features/doctors/pages/DoctorFormPage";
import DoctorDetailPage from "./features/doctors/pages/DoctorDetailPage";

// Consultations
import ConsultationListPage from "./features/consultations/pages/ConsultationListPage";
import ConsultationFormPage from "./features/consultations/pages/ConsultationFormPage";
import ConsultationDetailPage from "./features/consultations/pages/ConsultationDetailPage";

// Surgery
import SurgicalProcedureListPage from "./features/surgery/pages/SurgicalProcedureListPage";
import SurgicalProcedureFormPage from "./features/surgery/pages/SurgicalProcedureFormPage";
import SurgicalProcedureDetailPage from "./features/surgery/pages/SurgicalProcedureDetailPage";
import SurgeonDashboardPage from "./features/surgery/pages/SurgeonDashboardPage";
import PostOpPrescriptionPrint from "./features/surgery/pages/PostOpPrescriptionPrint";
import InformedConsentPrint from "./features/surgery/pages/InformedConsentPrint";

// Print views (outside DashboardLayout)
import ConsultationPrintPage from "./features/consultations/pages/ConsultationPrintPage";
import PrescriptionPrintPage from "./features/consultations/pages/PrescriptionPrintPage";

// Reports
import ReportsPage from "./features/reports/pages/ReportsPage";

// Billing
import BillingListPage from "./features/billing/pages/BillingListPage";
import InvoicePage from "./features/billing/pages/InvoicePage";

// Pre-Authorizations
import { PreAuthorizationListPage, PreAuthorizationFormPage, PreAuthorizationDetailPage } from "./features/pre-authorizations";

// ARS Claims
import { ClaimListPage, ClaimFormPage, ClaimDetailPage } from "./features/claims";

// Users & Profiles (admin)
import UserListPage from "./features/users/pages/UserListPage";
import UserFormPage from "./features/users/pages/UserFormPage";
import ProfileListPage from "./features/users/pages/ProfileListPage";
import ProfileFormPage from "./features/users/pages/ProfileFormPage";

// Settings
import { SettingsManagementPage } from "./features/settings";
import Setting from "./features/settings/components/Setting";

// Users, Roles, Profiles
import { RoleListPage, RoleFormPage } from "./features/users";

// Module config for conditional routing
import { isModuleEnabled } from './config/modules';

// Patient Portal
import PortalLayout from "./features/portal/components/PortalLayout";
import PortalProfilePage from "./features/portal/pages/PortalProfilePage";
import PortalAppointmentsPage from "./features/portal/pages/PortalAppointmentsPage";
import PortalHistoryPage from "./features/portal/pages/PortalHistoryPage";
import BookAppointmentPage from "./features/portal/pages/BookAppointmentPage";

// PQRS (lazy loaded when module enabled)
const PqrsListPage = lazy(() => import("./features/pqrs/pages/PqrsListPage"));
const PqrsDetailPage = lazy(() => import("./features/pqrs/pages/PqrsDetailPage"));
const PqrsCreatePage = lazy(() => import("./features/pqrs/pages/PqrsCreatePage"));
const PqrsAdminPage = lazy(() => import("./features/pqrs/pages/PqrsAdminPage"));

function gated(modules: string[], page: React.ReactElement) {
  return <ProtectedRoute requiredModules={modules}>{page}</ProtectedRoute>;
}

const AppRouter = () => {
  return (
    <ErrorBoundary fallback={ServerError}>
    <AuthProvider>
      <OfflineProvider>
        <BrowserRouter basename="/">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/register" element={<Login />} />
          <Route path="/changepassword" element={<ChangePassword />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Dashboard Layout */}
          <Route element={<DashboardLayout />}>
            <Route path="/settings" element={<Navigate to="/clinic-settings" replace />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />

            {/* Dashboards */}
            <Route path="/admin-dashboard" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/doctor-dashboard" element={<ProtectedRoute doctorOnly><DoctorDashboardPage /></ProtectedRoute>} />
            <Route path="/clinical-dashboard" element={gated(['consultations'], <ClinicalDashboardPage />)} />

            {/* Consultations — module gate, not just "logged in" */}
            <Route path="/consultations" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationListPage /></ProtectedRoute>} />
            <Route path="/consultations/new" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationFormPage /></ProtectedRoute>} />
            <Route path="/consultations/:id" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationDetailPage /></ProtectedRoute>} />
            {/* SignReviewPage (design.md Frontend section, task 3.5): the pending-signature
                queue links here. Reuses ConsultationDetailPage rather than duplicating its
                review UI — it already renders the full record read-only-aware and offers
                "Firmar consulta" (useCanSign) once the record is loaded. */}
            <Route path="/consultations/:id/review" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationDetailPage /></ProtectedRoute>} />
            <Route path="/consultations/:id/edit" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationFormPage /></ProtectedRoute>} />

            {/* Surgery - Protected */}
            <Route path="/surgery" element={gated(['surgery'], <SurgicalProcedureListPage />)} />
            <Route path="/surgery/new" element={gated(['surgery'], <SurgicalProcedureFormPage />)} />
            <Route path="/surgery/:id" element={gated(['surgery'], <SurgicalProcedureDetailPage />)} />
            <Route path="/surgery/:id/edit" element={gated(['surgery'], <SurgicalProcedureFormPage />)} />

            {/* Doctors - Protected */}
            <Route path="/doctors" element={gated(['doctors'], <DoctorListPage />)} />
            <Route path="/doctors/new" element={gated(['doctors'], <DoctorFormPage />)} />
            <Route path="/doctors/:id" element={gated(['doctors'], <DoctorDetailPage />)} />
            <Route path="/doctors/:id/edit" element={gated(['doctors'], <DoctorFormPage />)} />

            {/* Patients - Protected */}
            <Route path="/patients" element={gated(['patients'], <PatientListPage />)} />
            <Route path="/patients/list" element={<Navigate to="/patients" replace />} />
            <Route path="/patients/new" element={gated(['patients'], <PatientRegisterPage />)} />
            <Route path="/patients/:id" element={gated(['patients'], <PatientDetailPage />)} />
            <Route path="/patients/:id/edit" element={gated(['patients'], <PatientEditPage />)} />
            <Route path="/patientsprofile" element={<Navigate to="/patients" replace />} />

            {/* Appointments/Calendar - Protected */}
            <Route path="/appointments" element={gated(['appointments'], <AppointmentListPage />)} />
            <Route path="/appointments/new" element={gated(['appointments'], <AppointmentFormPage />)} />
            <Route path="/appointments/:id" element={gated(['appointments'], <AppointmentDetailPage />)} />

            {/* Pharmacy/Medications - Protected */}
            {isModuleEnabled('pharmacy') && (
            <>
            <Route path="/pharmacy" element={gated(['pharmacy'], <MedicationListPage />)} />
            <Route path="/pharmacy/medications" element={gated(['pharmacy'], <MedicationListPage />)} />
            <Route path="/pharmacy/medications/new" element={gated(['pharmacy'], <MedicationFormPage />)} />
            <Route path="/medications" element={gated(['pharmacy'], <MedicationListPage />)} />
            <Route path="/add-medication" element={gated(['pharmacy'], <MedicationFormPage />)} />
            <Route path="/edit-medication" element={gated(['pharmacy'], <MedicationFormPage />)} />
            </>
            )}

            {/* Insurance - Protected */}
            <Route path="/insurance-companies" element={gated(['insurance'], <InsuranceCompanyListPage />)} />
            <Route path="/insurance-companies/new" element={gated(['insurance'], <InsuranceCompanyFormPage />)} />
            <Route path="/add-insurance-company" element={gated(['insurance'], <InsuranceCompanyFormPage />)} />
            <Route path="/edit-insurance-company" element={gated(['insurance'], <InsuranceCompanyFormPage />)} />
            <Route path="/insurance-plans" element={gated(['insurance'], <InsurancePlanListPage />)} />
            <Route path="/insurance-plans/new" element={gated(['insurance'], <InsurancePlanFormPage />)} />
            <Route path="/add-insurance-plan" element={gated(['insurance'], <InsurancePlanFormPage />)} />
            <Route path="/edit-insurance-plan" element={gated(['insurance'], <InsurancePlanFormPage />)} />

            <Route path="/ncf-sequences" element={<ProtectedRoute adminOnly><NcfSequenceListPage /></ProtectedRoute>} />
            <Route path="/ncf-sequences/new" element={<ProtectedRoute adminOnly><NcfSequenceFormPage /></ProtectedRoute>} />
            <Route path="/ncf-sequences/:id/edit" element={<ProtectedRoute adminOnly><NcfSequenceFormPage /></ProtectedRoute>} />
            <Route path="/add-ncf-sequence" element={<ProtectedRoute adminOnly><NcfSequenceFormPage /></ProtectedRoute>} />
            <Route path="/edit-ncf-sequence" element={<ProtectedRoute adminOnly><NcfSequenceFormPage /></ProtectedRoute>} />

            <Route path="/suppliers" element={<ProtectedRoute adminOnly><SupplierListPage /></ProtectedRoute>} />
            <Route path="/suppliers/new" element={<ProtectedRoute adminOnly><SupplierFormPage /></ProtectedRoute>} />
            <Route path="/edit-supplier" element={<ProtectedRoute adminOnly><SupplierFormPage /></ProtectedRoute>} />

            {/* Services - Protected */}
            <Route path="/services" element={gated(['services'], <ServiceListPage />)} />
            <Route path="/services/new" element={gated(['services'], <ServiceFormPage />)} />
            <Route path="/add-service" element={gated(['services'], <ServiceFormPage />)} />
            <Route path="/edit-service" element={gated(['services'], <ServiceFormPage />)} />

            {/* Imaging - Protected */}
            {isModuleEnabled('imaging') && (
              <>
              <Route path="/imaging" element={gated(['imaging'], <ImagingOrderListPage />)} />
              <Route path="/imaging/new" element={gated(['imaging'], <ImagingOrderFormPage />)} />
              <Route path="/imaging-order/:id" element={gated(['imaging'], <ImagingResultFormPage />)} />
              </>
            )}

            {/* Administration - Protected (Admin Only) */}
            <Route path="/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/users/new" element={<Navigate to="/admin/users/new" replace />} />
            <Route path="/users/:id/edit" element={<Navigate to="/admin/users" replace />} />
            <Route path="/roles" element={<ProtectedRoute adminOnly><RoleListPage /></ProtectedRoute>} />
            <Route path="/roles/new" element={<ProtectedRoute adminOnly><RoleFormPage /></ProtectedRoute>} />
            <Route path="/roles/:id/edit" element={<ProtectedRoute adminOnly><RoleFormPage /></ProtectedRoute>} />
            <Route path="/profiles" element={<Navigate to="/admin/profiles" replace />} />
            <Route path="/profiles/new" element={<Navigate to="/admin/profiles/new" replace />} />
            <Route path="/profiles/:id/edit" element={<Navigate to="/admin/profiles/:id/edit" replace />} />

            {/* Billing - Protected */}
            <Route path="/billing" element={gated(['billing'], <BillingListPage />)} />
            <Route path="/billing/new" element={gated(['billing'], <InvoicePage />)} />
            <Route path="/billing/:id" element={gated(['billing'], <InvoicePage />)} />

            {/* Pre-Authorizations */}
            <Route path="/pre-authorizations" element={gated(['insurance'], <PreAuthorizationListPage />)} />
            <Route path="/pre-authorizations/new" element={gated(['insurance'], <PreAuthorizationFormPage />)} />
            <Route path="/pre-authorizations/:id" element={gated(['insurance'], <PreAuthorizationDetailPage />)} />

            {/* ARS Claims */}
            <Route path="/claims" element={gated(['insurance'], <ClaimListPage />)} />
            <Route path="/claims/new" element={gated(['insurance'], <ClaimFormPage />)} />
            <Route path="/claims/:id" element={gated(['insurance'], <ClaimDetailPage />)} />

            {/* Admin: Users & Profiles (new feature pages) */}
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><UserListPage /></ProtectedRoute>} />
            <Route path="/admin/users/new" element={<ProtectedRoute adminOnly><UserFormPage /></ProtectedRoute>} />
            <Route path="/admin/users/:id/edit" element={<ProtectedRoute adminOnly><UserFormPage /></ProtectedRoute>} />
            <Route path="/admin/profiles" element={<ProtectedRoute adminOnly><ProfileListPage /></ProtectedRoute>} />
            <Route path="/admin/profiles/new" element={<ProtectedRoute adminOnly><ProfileFormPage /></ProtectedRoute>} />
            <Route path="/admin/profiles/:id/edit" element={<ProtectedRoute adminOnly><ProfileFormPage /></ProtectedRoute>} />

            {/* Reports - Protected */}
            {isModuleEnabled('reports') && (
              <Route path="/reports" element={gated(['reports'], <ReportsPage />)} />
            )}

            {/* Settings - Protected */}
            <Route path="/settings-management" element={<ProtectedRoute adminOnly><SettingsManagementPage /></ProtectedRoute>} />
            <Route path="/clinic-settings" element={gated(['settings'], <Setting />)} />

            {/* Accounting Module - frontend pages not yet implemented; routes
                will be re-enabled when the pages land. See comment near the top
                of this file. */}
          </Route>

          {/* Print views — standalone, no layout */}
          <Route path="/consultations/:id/print" element={<ProtectedRoute requiredModules={['consultations']}><ConsultationPrintPage /></ProtectedRoute>} />
          <Route path="/consultations/:id/prescription" element={<ProtectedRoute requiredModules={['consultations']}><PrescriptionPrintPage /></ProtectedRoute>} />
          <Route path="/surgery/:id/post-op-prescription/print" element={gated(['surgery'], <PostOpPrescriptionPrint />)} />
          <Route path="/surgery/:id/informed-consent/print" element={gated(['surgery'], <InformedConsentPrint />)} />

            {/* Dashboard standalone pages */}
            <Route path="/surgeon-dashboard" element={gated(['surgery'], <SurgeonDashboardPage />)} />

            {/* Imaging Viewer — standalone with its own layout */}
            <Route path="/imaging-viewer" element={gated(['imaging'], <ImagingViewerPage />)} />

            {/* Patient Portal Routes — gated by VITE_MODULE_PATIENT_PORTAL */}
            {isModuleEnabled('patient_portal') && (
              <Route element={<PatientProtectedRoute><PortalLayout /></PatientProtectedRoute>}>
                <Route path="/portal/me" element={<PortalProfilePage />} />
                <Route path="/portal/appointments" element={<PortalAppointmentsPage />} />
                <Route path="/portal/history" element={<PortalHistoryPage />} />
                <Route path="/portal/book-appointment" element={<BookAppointmentPage />} />
                <Route path="/portal" element={<Navigate to="/portal/me" replace />} />
              </Route>
            )}

            {/* PQRS Module — gated by VITE_MODULE_PQRS */}
            {isModuleEnabled('pqrs') && (
              <>
                <Route path="/pqrs" element={gated(['pqrs'], <PqrsListPage />)} />
                <Route path="/pqrs/new" element={gated(['pqrs'], <PqrsCreatePage />)} />
                <Route path="/pqrs/:id" element={gated(['pqrs'], <PqrsDetailPage />)} />
                <Route path="/pqrs/admin/types" element={<ProtectedRoute adminOnly><PqrsAdminPage /></ProtectedRoute>} />
              </>
            )}

            <Route path="*" element={<Error />} />
          </Routes>
      </BrowserRouter>
      <div className="sidebar-overlay"></div>
      </OfflineProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
};

export default AppRouter;
